import { lazy, type ReactNode, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, Route, Routes } from 'react-router';
import { isDirector, useMe, useMembership } from './auth/session';
import { AdminLayout } from './components/AdminLayout';
import { AppLayout } from './components/AppLayout';
import { MaintenanceLayer } from './maintenance/MaintenanceLayer';
import { Forbidden, Loading, RequireAuth, RequireRole } from './components/Guards';
import { ChoristHomePage } from './pages/ChoristHomePage';
import { EventFormPage } from './pages/EventFormPage';
import { EventLineupPage } from './pages/EventLineupPage';
import { EventPage } from './pages/EventPage';
import { EventsPage } from './pages/EventsPage';
import { ChoristLibraryPage } from './pages/ChoristLibraryPage';
import { LibraryPage } from './pages/LibraryPage';
import { RehearsalPage } from './pages/RehearsalPage';
import { RepertoirePage } from './pages/RepertoirePage';
import { JoinPage } from './pages/JoinPage';
import { LoginPage } from './pages/LoginPage';
import { MyEventPage } from './pages/MyEventPage';
import { MyEventsPage } from './pages/MyEventsPage';
import { MembersPage } from './pages/MembersPage';
import { MyFeedbackPage } from './pages/MyFeedbackPage';
import { SongUploadPage } from './pages/SongUploadPage';
import { SetupPage } from './pages/SetupPage';
import { AdminAuditPage } from './pages/admin/AdminAuditPage';
import { AdminChoirsPage } from './pages/admin/AdminChoirsPage';
import { AdminFeedbackPage } from './pages/admin/AdminFeedbackPage';
import { AdminUsersPage } from './pages/admin/AdminUsersPage';
import ui from './styles/ui.module.css';

// The practice screen pulls in OpenSheetMusicDisplay and Tone.js: load it only when needed.
const PracticePage = lazy(() => import('./pages/PracticePage').then((m) => ({ default: m.PracticePage })));
const TakeResultsPage = lazy(() => import('./pages/TakeResultsPage').then((m) => ({ default: m.TakeResultsPage })));
const ReviewPage = lazy(() => import('./pages/ReviewPage').then((m) => ({ default: m.ReviewPage })));
const HeadsetCheckPage = lazy(() => import('./pages/HeadsetCheckPage').then((m) => ({ default: m.HeadsetCheckPage })));

/** Etkinlikler: the director plans events; everyone else sees theirs, read-only. */
function Events() {
  const membership = useMembership();
  return isDirector(membership) ? <EventsPage /> : <MyEventsPage />;
}

function Event() {
  const membership = useMembership();
  return isDirector(membership) ? <EventPage /> : <MyEventPage />;
}

/** Kitaplık: the director manages the songs there; everyone else sees them with their own standing. */
function Library() {
  const membership = useMembership();
  return isDirector(membership) ? <LibraryPage /> : <ChoristLibraryPage />;
}

/** The home screen depends on the role: the director lands in the library, everyone else on their songs. */
function Home() {
  const membership = useMembership();
  return isDirector(membership) ? <Navigate to="/library" replace /> : <ChoristHomePage />;
}

/** The administrator's own pages; during "view as" they wait until the viewing ends. */
function RequireAdmin({ children }: { children: ReactNode }) {
  const me = useMe().data;
  if (me?.admin && me.viewAs) return <Navigate to="/" replace />;
  if (!me?.admin) return <Forbidden />;
  return <>{children}</>;
}

/** The choir pages: the administrator sees them only while viewing as someone. */
function MemberArea({ children }: { children: ReactNode }) {
  const me = useMe().data;
  if (me?.admin && !me.viewAs) return <Navigate to="/admin/users" replace />;
  return <>{children}</>;
}

function NotFound() {
  const { t } = useTranslation();
  return (
    <div className={ui.page}>
      <div className={ui.card}>
        <p>{t('common.notFound')}</p>
        <Link to="/">{t('common.backHome')}</Link>
      </div>
    </div>
  );
}

export function App() {
  return (
    <>
      {/* The deploy's maintenance: countdown banner, or the notice (maintenance/MaintenanceLayer.tsx). */}
      <MaintenanceLayer />
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* An invitation link: open without signing in (wireframe 15). */}
      <Route path="/join/:token" element={<JoinPage />} />
      {/* A one-time account link: setup or a new password (and the administrator's authenticator). */}
      <Route path="/setup/:token" element={<SetupPage />} />
      <Route
        path="/admin"
        element={
          <RequireAuth>
            <RequireAdmin>
              <AdminLayout />
            </RequireAdmin>
          </RequireAuth>
        }
      >
        <Route index element={<Navigate to="/admin/users" replace />} />
        <Route path="users" element={<AdminUsersPage />} />
        <Route path="choirs" element={<AdminChoirsPage />} />
        <Route path="audit" element={<AdminAuditPage />} />
        <Route path="feedback" element={<AdminFeedbackPage />} />
        <Route path="*" element={<NotFound />} />
      </Route>
      <Route
        element={
          <RequireAuth>
            <MemberArea>
              <AppLayout />
            </MemberArea>
          </RequireAuth>
        }
      >
        <Route index element={<Home />} />
        <Route path="library" element={<Library />} />
        <Route
          path="library/upload"
          element={
            <RequireRole anyOf={['DIRECTOR']}>
              <SongUploadPage />
            </RequireRole>
          }
        />
        <Route path="events" element={<Events />} />
        <Route
          path="events/new"
          element={
            <RequireRole anyOf={['DIRECTOR']}>
              <EventFormPage />
            </RequireRole>
          }
        />
        <Route path="events/:eventId" element={<Event />} />
        {/* Rehearsal mode: every member and the director. */}
        <Route path="events/:eventId/rehearsal" element={<RehearsalPage />} />
        <Route
          path="events/:eventId/edit"
          element={
            <RequireRole anyOf={['DIRECTOR']}>
              <EventFormPage />
            </RequireRole>
          }
        />
        <Route
          path="events/:eventId/lineup"
          element={
            <RequireRole anyOf={['DIRECTOR']}>
              <EventLineupPage />
            </RequireRole>
          }
        />
        <Route
          path="events/:eventId/repertoire"
          element={
            <RequireRole anyOf={['DIRECTOR']}>
              <RepertoirePage />
            </RequireRole>
          }
        />
        <Route path="members" element={<MembersPage />} />
        <Route path="feedback" element={<MyFeedbackPage />} />
        {/* The chorists' list used to live at /songs. */}
        <Route path="songs" element={<Navigate to="/library" replace />} />
        <Route
          path="songs/:songId"
          element={
            <Suspense fallback={<Loading />}>
              <PracticePage />
            </Suspense>
          }
        />
        <Route
          path="songs/:songId/takes/:takeId"
          element={
            <Suspense fallback={<Loading />}>
              <TakeResultsPage />
            </Suspense>
          }
        />
        <Route
          path="review"
          element={
            <RequireRole anyOf={['DIRECTOR']}>
              <Suspense fallback={<Loading />}>
                <ReviewPage />
              </Suspense>
            </RequireRole>
          }
        />
        <Route
          path="review/:takeId"
          element={
            <RequireRole anyOf={['DIRECTOR']}>
              <Suspense fallback={<Loading />}>
                <ReviewPage />
              </Suspense>
            </RequireRole>
          }
        />
        <Route
          path="headset"
          element={
            <Suspense fallback={<Loading />}>
              <HeadsetCheckPage />
            </Suspense>
          }
        />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
    </>
  );
}
